"use client";

import * as React from "react";
import {
  AppWindow,
  Download,
  Globe,
  Laptop,
  Monitor,
  MonitorSmartphone,
  Smartphone,
  Store,
  Tv,
  type LucideIcon,
} from "lucide-react";
import type { AppStoreLinkKey } from "@gerama/types";
import { cn } from "@gerama/utils";
import { BRAND } from "@/lib/brand";

type Device = "android" | "ios" | "windows" | "mac" | "linux" | null;

interface Option {
  key: string;
  title: string;
  hint: string;
  icon: LucideIcon;
  /** Which admin setting (Platform settings → stores) holds the link. */
  link: AppStoreLinkKey;
  /** Which device this is the natural pick for — that card gets highlighted. */
  device?: Exclude<Device, null>;
  external?: boolean;
}

interface Group {
  title: string;
  description: string;
  icon: LucideIcon;
  options: Option[];
}

const GROUPS: Group[] = [
  {
    title: "موبایل",
    description: "اندروید و آیفون",
    icon: Smartphone,
    options: [
      {
        key: "bazaar",
        title: "کافه‌بازار",
        hint: "نصب و به‌روزرسانی خودکار",
        icon: Store,
        link: "bazaar",
        device: "android",
        external: true,
      },
      {
        key: "myket",
        title: "مایکت",
        hint: "نصب و به‌روزرسانی خودکار",
        icon: Store,
        link: "myket",
        device: "android",
        external: true,
      },
      {
        key: "apk",
        title: "دانلود مستقیم",
        hint: "فایل نصبی اندروید (APK)",
        icon: Download,
        link: "apk",
        device: "android",
      },
      {
        key: "appstore",
        title: "اپ‌استور",
        hint: "آیفون و آیپد",
        icon: Store,
        link: "appstore",
        device: "ios",
        external: true,
      },
    ],
  },
  {
    title: "کامپیوتر",
    description: "برنامهٔ دسکتاپ با کلیدهای میان‌بر و کنترل پخش سیستم",
    icon: Laptop,
    options: [
      {
        key: "windows",
        title: "ویندوز",
        hint: "ویندوز ۱۰ و ۱۱",
        icon: Monitor,
        link: "windows",
        device: "windows",
      },
      {
        key: "mac",
        title: "مک",
        hint: "macOS",
        icon: Laptop,
        link: "mac",
        device: "mac",
      },
      {
        key: "linux",
        title: "لینوکس",
        hint: "AppImage",
        icon: AppWindow,
        link: "linux",
        device: "linux",
      },
    ],
  },
  {
    title: "تلویزیون",
    description: "صفحهٔ بزرگ، کنترل با ریموت",
    icon: Tv,
    options: [
      {
        key: "android-tv",
        title: "اندروید تی‌وی",
        hint: "تلویزیون‌های هوشمند و اندروید باکس",
        icon: Tv,
        link: "androidTv",
      },
      {
        key: "tv-web",
        title: "نسخهٔ وب تلویزیون",
        hint: "در مرورگر هر تلویزیون هوشمند",
        icon: Globe,
        link: "tvWeb",
        external: true,
      },
    ],
  },
];

function detectDevice(): Device {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/windows/i.test(ua)) return "windows";
  if (/mac os x|macintosh/i.test(ua)) return "mac";
  if (/linux/i.test(ua)) return "linux";
  return null;
}

/** Chrome/Edge/Android hand us this event when the site can be installed as an app. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type DownloadLinks = Partial<Record<AppStoreLinkKey, string>>;

export function DownloadOptions({ links }: { links: DownloadLinks }) {
  const [device, setDevice] = React.useState<Device>(null);
  const [install, setInstall] = React.useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = React.useState(false);

  React.useEffect(() => {
    setDevice(detectDevice());
    setInstalled(window.matchMedia("(display-mode: standalone)").matches);
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstall(e as InstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return (
    <div className="space-y-10">
      <section className="from-primary/30 via-primary/10 relative overflow-hidden rounded-3xl bg-gradient-to-bl to-transparent p-6 sm:p-10">
        <div className="max-w-xl space-y-3">
          <span className="solid-primary text-primary-foreground inline-flex size-14 items-center justify-center rounded-2xl shadow-lg">
            <MonitorSmartphone className="size-7" />
          </span>
          <h1 className="text-3xl font-black sm:text-4xl">{BRAND} همه‌جا با شماست</h1>
          <p className="text-muted-foreground text-base leading-7">
            روی گوشی، کامپیوتر و تلویزیون نصبش کنید و از همان جایی که بودید ادامه دهید. کتابخانه،
            پلی‌لیست‌ها و اشتراک شما روی همهٔ دستگاه‌ها یکی است.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <GroupHeader
          icon={AppWindow}
          title="وب‌اپ"
          description="بدون نصب از فروشگاه — مستقیم از همین مرورگر"
        />
        <div className="bg-card flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-bold">نصب {BRAND} روی صفحهٔ اصلی</p>
            <p className="text-muted-foreground text-sm leading-6">
              {installed
                ? "وب‌اپ روی این دستگاه نصب است."
                : install
                  ? "با یک لمس مثل یک برنامه نصب می‌شود و بدون نوار مرورگر باز می‌شود."
                  : device === "ios"
                    ? "در سافاری دکمهٔ «اشتراک‌گذاری» را بزنید و «افزودن به صفحهٔ اصلی» را انتخاب کنید."
                    : "از منوی مرورگر گزینهٔ «نصب برنامه» یا «افزودن به صفحهٔ اصلی» را انتخاب کنید."}
            </p>
          </div>
          {install && !installed && (
            <button
              type="button"
              onClick={async () => {
                await install.prompt();
                const choice = await install.userChoice;
                if (choice.outcome === "accepted") setInstalled(true);
                setInstall(null);
              }}
              className="solid-primary text-primary-foreground inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold transition-transform active:scale-95"
            >
              <Download className="size-4" />
              نصب وب‌اپ
            </button>
          )}
        </div>
      </section>

      {GROUPS.map((group) => (
        <section key={group.title} className="space-y-4">
          <GroupHeader icon={group.icon} title={group.title} description={group.description} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {group.options.map((option) => {
              const href = links[option.link] || undefined;
              return (
                <OptionCard
                  key={option.key}
                  option={option}
                  href={href}
                  suggested={!!href && !!device && option.device === device}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function GroupHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="bg-foreground/5 flex size-10 items-center justify-center rounded-xl">
        <Icon className="size-5" />
      </span>
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
    </div>
  );
}

function OptionCard({
  option,
  href,
  suggested,
}: {
  option: Option;
  href: string | undefined;
  suggested: boolean;
}) {
  const Icon = option.icon;
  const body = (
    <>
      <span
        className={cn(
          "flex size-11 shrink-0 items-center justify-center rounded-xl",
          suggested ? "solid-primary text-primary-foreground" : "bg-foreground/5",
        )}
      >
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 font-bold">
          {option.title}
          {suggested && (
            <span className="bg-primary/15 text-primary rounded-full px-2 py-0.5 text-[11px] font-semibold">
              مناسب این دستگاه
            </span>
          )}
        </span>
        <span className="text-muted-foreground block truncate text-xs">{option.hint}</span>
      </span>
      {href ? (
        <Download className="text-muted-foreground size-4 shrink-0" />
      ) : (
        <span className="text-muted-foreground bg-foreground/5 shrink-0 rounded-full px-2 py-0.5 text-[11px]">
          به‌زودی
        </span>
      )}
    </>
  );
  const className = cn(
    "bg-card flex items-center gap-3 rounded-2xl p-4 transition-[transform,background-color]",
    href ? "hover:bg-accent active:scale-[0.98]" : "opacity-60",
    suggested && "ring-primary/60 ring-2",
  );
  if (!href) {
    return (
      <div className={className} aria-disabled>
        {body}
      </div>
    );
  }
  return (
    <a
      href={href}
      className={className}
      {...(option.external ? { target: "_blank", rel: "noreferrer" } : { download: "" })}
    >
      {body}
    </a>
  );
}
